import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, Calendar, CheckCircle2, Compass, Copy, Download,
  FileText, MessageSquare, Play, Plus, Sparkles, Target, Trophy, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ModuleTitleRHYTHM from "@/components/rhythm/ModuleTitleRHYTHM";
import AgendaQuickAdd from "@/components/rhythm/AgendaQuickAdd";
import AgendaAuthorChip from "@/components/rhythm/AgendaAuthorChip";

const HEALTH_CHIP = { green: "patria-chip-green", yellow: "patria-chip-terra", red: "patria-chip-terra" };
const HEALTH_LABEL = { green: "On Cadence", yellow: "Slipping", red: "Cadence Red" };

export default function RhythmWorkspace() {
  const { pairingId } = useParams();
  const { user, backendUrl } = useAuth();
  const navigate = useNavigate();
  const [pairing, setPairing] = useState(null);
  const [agenda, setAgenda] = useState([]);
  const [actions, setActions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [newAction, setNewAction] = useState({ title: "", owner_id: "", due_date: "" });
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [populating, setPopulating] = useState(false);

  const viewerRole = pairing?.viewer_role || "none";
  const isSupervisor = viewerRole === "supervisor";
  const isEmployee = viewerRole === "employee";
  const isOversight = viewerRole === "oversight";  // PO/Super Admin on someone else's pairing
  const isPastorMeta = viewerRole === "pastor_metadata";
  // Content mutations are participant-only. PO/Super Admin viewing someone
  // else's pairing gets a READ-ONLY view (view records, view agenda, export
  // PDF, see audit trail — no controls).
  const canMutate = isSupervisor || isEmployee;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, a, ai, ml] = await Promise.all([
        api.get(`/rhythm/pairings/${pairingId}`),
        api.get(`/rhythm/pairings/${pairingId}/agenda`),
        api.get(`/rhythm/pairings/${pairingId}/action-items`),
        api.get(`/rhythm/pairings/${pairingId}/meetings`),
      ]);
      setPairing(p.data);
      setAgenda(a.data);
      setActions(ai.data);
      setMeetings(ml.data);
      // Growth plan only when the viewer is a participant OR a PO admin.
      const viewerRoleFresh = p.data.viewer_role || "none";
      if (["supervisor", "employee", "oversight"].includes(viewerRoleFresh)) {
        try {
          const g = await api.get(`/rhythm/growth-plans/${p.data.employee_id}`);
          setGrowth(g.data);
        } catch (gerr) {
          // pastor_metadata viewers get 403 here — expected. Log at debug
          // level so real errors don't get swallowed silently.
          if (gerr?.response?.status !== 403) {
            console.error("growth plan load failed", gerr);
          }
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load this workspace.");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [pairingId]);

  // Mark this pairing "seen" once the workspace has loaded so the unread
  // badge clears the next time the user hits the landing/dashboard.
  useEffect(() => {
    if (!pairing || isPastorMeta) return;
    api.post(`/rhythm/pairings/${pairingId}/mark-seen`).catch((err) => {
      // Non-critical — the unread badge just won't clear until next load.
      console.error("mark-seen failed", err);
    });
    /* eslint-disable-next-line */
  }, [pairing?.id]);

  const handleAgendaAdded = (item) => {
    setAgenda((prev) => [...prev, item]);
  };

  const populateStarter = async () => {
    setPopulating(true);
    try {
      const { data } = await api.post(`/rhythm/pairings/${pairingId}/agenda/populate-starter`);
      if (data.count === 0) {
        toast.success("Your agenda already has the starter items.");
      } else {
        toast.success(`Populated ${data.count} starter item${data.count === 1 ? "" : "s"}.`);
      }
      const a = await api.get(`/rhythm/pairings/${pairingId}/agenda`);
      setAgenda(a.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setPopulating(false);
    }
  };

  const copyAgendaLink = async () => {
    const link = `${window.location.origin}/rhythm/workspace/${pairingId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Agenda Link Copied — paste it into a message or email.");
    } catch {
      // Fallback: prompt() so tests without clipboard permission still work.
      window.prompt("Copy this agenda link:", link);
    }
  };

  const archiveAgenda = async (id) => {
    try { await api.post(`/rhythm/agenda/${id}/archive`); setAgenda((prev) => prev.filter((x) => x.id !== id)); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const startMeeting = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/rhythm/pairings/${pairingId}/meetings`);
      navigate(`/rhythm/meeting/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setStarting(false); }
  };

  const addAction = async (e) => {
    e.preventDefault();
    if (!newAction.title.trim() || !newAction.owner_id) { toast.error("Title + owner required."); return; }
    try {
      await api.post(`/rhythm/pairings/${pairingId}/action-items`, {
        title: newAction.title, owner_id: newAction.owner_id,
        due_date: newAction.due_date || null,
      });
      setNewAction({ title: "", owner_id: "", due_date: "" });
      const ai = await api.get(`/rhythm/pairings/${pairingId}/action-items`);
      setActions(ai.data);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const toggleAction = async (a) => {
    const newStatus = a.status === "done" ? "open" : "done";
    try {
      await api.put(`/rhythm/action-items/${a.id}`, { status: newStatus });
      const ai = await api.get(`/rhythm/pairings/${pairingId}/action-items`);
      setActions(ai.data);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const downloadPairingPDF = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/rhythm/pairings/${pairingId}/export.pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Unable to fetch");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rhythm-pairing-${pairingId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Couldn't export the pairing history."); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = actions.filter((a) => a.status === "open" && a.due_date && a.due_date < today).length;

  if (loading || !pairing) {
    return <p className="italic text-charcoal-muted p-6">Loading workspace…</p>;
  }

  return (
    <div className="space-y-6" data-testid="rhythm-workspace">
      <div>
        <Link to="/rhythm" className="text-sm text-forest-700 hover:text-forest-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To RHYTHM
        </Link>
        <ModuleTitleRHYTHM className="mt-2 patria-module-title text-lg sm:text-xl" />
        <div className="flex items-start justify-between gap-4 flex-wrap mt-2">
          <div>
            <h1 className="text-4xl font-serif text-forest-900">
              {pairing.supervisor_name} + {pairing.employee_name}
            </h1>
            <p className="mt-2 text-sm text-charcoal-soft">
              Cadence: {pairing.effective_cadence.charAt(0).toUpperCase() + pairing.effective_cadence.slice(1)}
              {pairing.compass_override && (
                <span className="inline-flex items-center gap-1 ml-2 text-terracotta-600">
                  <Compass className="h-3.5 w-3.5" strokeWidth={1.5} /> COMPASS Override
                </span>
              )}
              &nbsp;· Next scheduled: {pairing.next_scheduled}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={HEALTH_CHIP[pairing.health]}>
              {pairing.health !== "green" && <AlertTriangle className="h-3 w-3 mr-1" />}
              {HEALTH_LABEL[pairing.health]}
            </span>
            <button
              className="patria-btn-secondary text-sm"
              onClick={downloadPairingPDF}
              data-testid="btn-export-pairing-pdf"
            >
              <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {isPastorMeta && (
        <div className="patria-card p-4 bg-terracotta-50 border-l-4 border-terracotta-500 text-sm text-charcoal-soft" data-testid="rhythm-pastor-meta-notice">
          You are viewing this pairing as a Campus Pastor — metadata only. Meeting note content is
          not shown unless you are the supervisor of record on this pairing.
        </div>
      )}
      {isOversight && (
        <div className="patria-card p-4 bg-forest-900/5 border-l-4 border-forest-700 text-sm text-charcoal-soft" data-testid="rhythm-oversight-notice">
          You are viewing this pairing as People Operations — <strong>read-only oversight</strong>.
          Only the supervisor and employee of record can start 1:1s, add agenda items, or flag
          direct feedback. Every access here is audit-logged.
        </div>
      )}
      {isEmployee && !isSupervisor && (
        <div className="patria-card p-4 bg-cream-100 border-l-4 border-terracotta-500 text-sm text-charcoal-soft" data-testid="rhythm-employee-notice">
          This is your own 1:1 with {pairing.supervisor_name}. Bring what you&rsquo;d like to talk about —
          your supervisor will start the record when you meet.
        </div>
      )}

      {/* Meeting quick-actions */}
      <section className="patria-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center">
              <MessageSquare className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900">Meeting Records</h2>
              <p className="text-xs text-charcoal-muted">{meetings.length} recorded so far.</p>
            </div>
          </div>
          {isSupervisor && (
            <button
              onClick={startMeeting}
              disabled={starting}
              className="patria-btn-primary"
              data-testid="btn-start-meeting"
            >
              <Play className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
              {starting ? "Starting…" : "Start 1:1"}
            </button>
          )}
        </div>
        <ul className="mt-4 divide-y divide-sage-300/60">
          {meetings.length === 0 && (
            <li className="py-6 text-sm text-charcoal-muted italic">No meetings yet — start your first 1:1.</li>
          )}
          {meetings.map((m) => {
            // Draft records are supervisor-only. Employees / oversight see a
            // placeholder row with no Open action.
            const isDraft = m.status === "in_progress";
            const openBlocked = isDraft && !isSupervisor;
            return (
            <li key={m.id} className="py-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-forest-900">
                  {(m.finalized_at || m.started_at || "").slice(0, 10)} · {m.status.replace("_", " ")}
                </p>
                <p className="text-xs text-charcoal-muted">
                  {openBlocked ? (
                    <span className="italic">1:1 in progress — {(m.started_at || "").slice(0, 10)} · not yet released</span>
                  ) : m.locked ? "Locked · addenda only" : m.status === "in_progress" ? "Draft" : "Awaiting acknowledgment"}
                  {m.direct_feedback_flag && !openBlocked && " · Direct Feedback Flagged"}
                  {m.compass_check_in && !openBlocked && " · COMPASS Check-In"}
                </p>
              </div>
              {openBlocked ? (
                <span className="text-xs text-charcoal-muted italic" data-testid={`open-meeting-blocked-${m.id}`}>
                  Draft — supervisor only
                </span>
              ) : (
                <Link to={`/rhythm/meeting/${m.id}`} className="patria-btn-secondary text-sm" data-testid={`open-meeting-${m.id}`}>
                  Open
                </Link>
              )}
            </li>
          );})}
        </ul>
      </section>

      {/* Agenda — only participants can add/archive. Oversight sees the list read-only. */}
      {(canMutate || isOversight) && !isPastorMeta && (
        <section className="patria-card p-6" data-testid="rhythm-agenda-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-forest-900">Shared Agenda</h2>
                <p className="text-sm text-charcoal-soft">
                  {canMutate
                    ? "Both of you can add topics anytime — press Enter and it's on the shared list."
                    : "Read-only view. The pairing's supervisor and employee own the agenda."}
                </p>
              </div>
            </div>
            {canMutate && (
              <button
                type="button"
                onClick={copyAgendaLink}
                className="patria-btn-ghost text-forest-700 text-sm"
                data-testid="btn-copy-agenda-link"
              >
                <Copy className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Copy Agenda Link
              </button>
            )}
          </div>

          {canMutate && (
            <div className="mt-4">
              <AgendaQuickAdd
                pairingId={pairingId}
                onAdded={handleAgendaAdded}
                testIdPrefix="workspace-quick-add"
              />
            </div>
          )}

          {canMutate && agenda.length === 0 && (
            <div className="mt-4 rounded-2xl bg-cream-100 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="agenda-empty-state">
              <div>
                <p className="text-forest-900 font-medium">Nothing on your agenda yet.</p>
                <p className="text-xs text-charcoal-muted">
                  Add the four warm starter topics with one click and grow from there.
                </p>
              </div>
              <button
                type="button"
                onClick={populateStarter}
                disabled={populating}
                className="patria-btn-primary shrink-0"
                data-testid="btn-populate-starter-agenda"
              >
                <Sparkles className="h-4 w-4 mr-1" strokeWidth={1.5} />
                {populating ? "Populating…" : "Populate A Starter Agenda"}
              </button>
            </div>
          )}

          <ul className="mt-4 space-y-2" data-testid="agenda-list">
            {agenda.map((it) => {
              const isSupervisorAuthor = String(it.added_by_id) === String(pairing?.supervisor_id);
              return (
                <li key={it.id} className="p-3 rounded-xl bg-cream-100 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <AgendaAuthorChip name={it.added_by_name} isSupervisor={isSupervisorAuthor} />
                    <div className="min-w-0">
                      <p className="text-forest-900 font-medium break-words">{it.title}</p>
                      {it.note && <p className="text-xs text-charcoal-muted break-words">{it.note}</p>}
                      <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-1">
                        {isSupervisorAuthor ? "Supervisor" : "Employee"} · Added By {it.added_by_name}
                        {it.date_added ? ` · ${String(it.date_added).slice(0, 10)}` : ""}
                      </p>
                    </div>
                  </div>
                  {canMutate && (
                    <button
                      onClick={() => archiveAgenda(it.id)}
                      className="patria-btn-ghost text-terracotta-600 shrink-0"
                      data-testid={`agenda-archive-${it.id}`}
                    >
                      <X className="h-4 w-4" strokeWidth={1.5} /> Archive
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Action items — only participants can add/edit; oversight sees read-only list */}
      {(canMutate || isOversight) && !isPastorMeta && (
        <section className="patria-card p-6" data-testid="rhythm-actions-section">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900">Action Items</h2>
              <p className="text-xs text-charcoal-muted">
                {actions.filter((a) => a.status === "open").length} open · {overdueCount} overdue
              </p>
            </div>
          </div>
          {canMutate && (
            <form onSubmit={addAction} className="mt-4 grid grid-cols-1 sm:grid-cols-6 gap-2">
              <input
                className="patria-input sm:col-span-3"
                placeholder="Action item…"
                value={newAction.title}
                onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                data-testid="action-title-input"
              />
              <select
                className="patria-input sm:col-span-1"
                value={newAction.owner_id}
                onChange={(e) => setNewAction({ ...newAction, owner_id: e.target.value })}
                data-testid="action-owner-select"
              >
                <option value="">Owner…</option>
                <option value={pairing.supervisor_id}>{pairing.supervisor_name}</option>
                <option value={pairing.employee_id}>{pairing.employee_name}</option>
              </select>
              <input
                type="date"
                className="patria-input sm:col-span-1"
                value={newAction.due_date}
                onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
                data-testid="action-due-input"
              />
              <button type="submit" className="patria-btn-primary" data-testid="btn-add-action">
                <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add
              </button>
            </form>
          )}
          <ul className="mt-4 space-y-2" data-testid="actions-list">
            {actions.length === 0 && <li className="text-sm text-charcoal-muted italic">No action items yet.</li>}
            {actions.map((a) => {
              const overdue = a.status === "open" && a.due_date && a.due_date < today;
              return (
                <li key={a.id} className="p-3 rounded-xl bg-cream-100 flex items-start justify-between gap-3" data-testid={`action-row-${a.id}`}>
                  <div className="flex items-start gap-3">
                    {canMutate ? (
                      <button
                        onClick={() => toggleAction(a)}
                        className={a.status === "done"
                          ? "h-5 w-5 rounded-full bg-forest-900 text-cream-100 flex items-center justify-center"
                          : "h-5 w-5 rounded-full border-2 border-forest-900/40"}
                        data-testid={`action-toggle-${a.id}`}
                        aria-label="Toggle done"
                      >
                        {a.status === "done" && <CheckCircle2 className="h-4 w-4" strokeWidth={2} />}
                      </button>
                    ) : (
                      <span className={a.status === "done" ? "h-5 w-5 rounded-full bg-forest-900" : "h-5 w-5 rounded-full border-2 border-forest-900/40"} />
                    )}
                    <div>
                      <p className={a.status === "done" ? "line-through text-charcoal-muted" : "text-forest-900 font-medium"}>
                        {a.title}
                      </p>
                      <p className="text-xs text-charcoal-muted">
                        {a.owner_name} · due {a.due_date || "—"} {overdue && <span className="text-terracotta-600 font-medium">· OVERDUE</span>}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Growth plan mini */}
      {growth && !isPastorMeta && (
        <section className="patria-card p-6" data-testid="rhythm-growth-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center">
                <Target className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-2xl text-forest-900">Growth Plan</h2>
            </div>
            <Link
              to={`/rhythm/growth/${pairing.employee_id}`}
              className="patria-btn-secondary text-sm"
              data-testid="btn-open-growth"
            >
              Open Full Plan
            </Link>
          </div>
          <ul className="mt-4 space-y-2">
            {(growth.goals || []).slice(0, 3).map((g) => (
              <li key={g.id} className="p-3 rounded-xl bg-cream-100">
                <p className="text-forest-900 font-medium">{g.description}</p>
                <p className="text-xs text-charcoal-muted">Status: {g.status.replace("_", " ")} · Target: {g.target_date || "—"}</p>
              </li>
            ))}
            {(growth.goals || []).length === 0 && <li className="text-sm text-charcoal-muted italic">No goals yet.</li>}
          </ul>
          <div className="mt-4 flex items-center gap-2 text-sm text-charcoal-soft">
            <Trophy className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
            <span>{(growth.wins || []).length} wins logged this season.</span>
          </div>
        </section>
      )}
    </div>
  );
}
